import { IJSONSchema } from "../../../../base/common/jsonSchema";
import { typeNameOfTarget } from "../targetLabel.js";
import { safeStringify } from "../../../../base/common/objects";
import { IHierarchyNode, IHierarchyService } from "../../../hierarchy/common/hierarchy";
import { IInspectionService, type InspectionTargetRef } from "../../../inspection/common/inspection";
import { IInstantiationService } from "../../../instantiation/common/instantiation";
import { ILogService } from "../../../log/common/log";
import { IPropertyService, PropertyScope, PropertyViewMode, type IProperty } from "../../../properties/common/properties";
import { resolvePropertyByPath } from "../../../properties/common/resolvePropertyPath";
import { IResourceService } from "../../../resources/common/resources";
import { AIToolCallResponse, FunctionAiTool, IAITool } from "../ai";
import { aiTargetFor, parseAiTarget, resolveAiTargetOrResource, type AiTarget, type TargetResolverLike } from "../target";
import { normalizeIncomingPath, splitAddress } from "../../../properties/common/propertyPath";
import { handleOf } from "../../../webgl/common/objectHandle";
import { IWebGLService } from "../../../webgl/common/webgl";
import { IObjectTargetService } from "../../../../editor/services/project/common/documentTarget";

// #region Find Properties Tool

/**
 * Rows that cost tokens and buy nothing: a value that stringified to a circular
 * placeholder or a raw pixel dump, and the read-only type flags three hangs off
 * every object (isTexture, isCompressedTexture, …) which a model can neither
 * interpret nor set.
 */
function isNoiseRow(property: { key: string; value?: unknown; editable?: boolean }): boolean {
    if (property.key === "mipmaps" || property.key === "image" || property.key === "source") return true;
    // three's type-brand flags: always true, never settable, one per subclass
    if (property.editable === false && /^is[A-Z]/.test(property.key)) return true;
    return false;
}

/** Keep a failed lazy child branch from aborting the entire query. Runtime
 * component context is an implementation graph, not a scene property. */
function* iterateQueryProperties(properties: Iterable<IProperty>, onError: (path: string, error: unknown) => void): Iterable<IProperty> {
    const stack: Array<{ iterator: Iterator<IProperty>; path: string }> = [{ iterator: properties[Symbol.iterator](), path: "" }];
    while (stack.length) {
        const frame = stack[stack.length - 1];
        let next: IteratorResult<IProperty>;
        try {
            next = frame.iterator.next();
        } catch (error) {
            onError(frame.path, error);
            stack.pop();
            continue;
        }
        if (next.done) {
            stack.pop();
            continue;
        }
        const property = next.value;
        const path = splitAddress(property.metadata?.address ?? "")?.path ?? [frame.path, property.key].filter(Boolean).join("/");
        if (property.key === "__context") continue;
        yield property;
        try {
            const children = property.children;
            if (children?.length) stack.push({ iterator: children[Symbol.iterator](), path });
        } catch (error) {
            onError(path, error);
        }
    }
}

/**
 * A value that SERIALIZES to a cycle marker carries nothing a caller can read or
 * set — `parent` on a scene node is the usual one.
 *
 * This has to run on the serialized form: "[Circular]" is produced by
 * safeStringify, not by the live property, so the old check against
 * `property.value` could never match and the rows went out regardless. (It also
 * used to catch merely SHARED references, which was safeStringify marking those
 * wrongly — fixed at the root in objects.ts.)
 */
function serializesToCycle(value: unknown): boolean {
    if (value === undefined) return false;
    return safeStringify(value, undefined, safeStringifyReplacer) === '"[Circular]"';
}

type FindPropertiesParams = {
    /** document targets from a search — the only way to reach a resource
     * (JSON strings tolerated: a model serializes objects) */
    targets?: Array<AiTarget | string>;
    /** tolerated alias: models borrow property_read's singular form — ignoring
     * it silently degraded to the inspection fallback (the wrong object) */
    target?: AiTarget | string;
    nodeQuery?: string;
    nodeType?: string;
    nodeMaxResults?: number;
    propertyQuery?: string;
    propertyType?: string;
    includeValues?: boolean;
    maxValueSize?: number;
    searchInDescriptions?: boolean;
    maxResults?: number;
    offset?: number;
}


export class FindPropertiesTool implements IAITool {
    readonly name = "property_find";
    readonly description = "Find property information of hierarchy nodes/objects in the scene. Three modes: (1) direct query by TARGETS from a search, (2) search for nodes first (nodeQuery), or (3) NO node params = whatever the user is currently inspecting — including Resources-panel picks like a geometry or texture, which are only reachable via targets or inspection. Both propertyQuery and propertyType support regex patterns (case-insensitive).";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            targets: {
                type: "array",
                description: "TARGETS from hierarchy_search_nodes / resources_list / property_find, passed back verbatim. The ONLY way to reach a material or texture (they are not hierarchy nodes). Never build one yourself — use nodeQuery to search by name instead.",
                items: {
                    type: "object",
                },
            },
            nodeQuery: {
                type: "string",
                description: "Find nodes by name first (e.g. 'player'). Comma-separated for multiple terms.",
            },
            nodeType: {
                type: "string",
                description: "Filter nodeQuery results by type. Regex: 'Mesh', 'Light.*'.",
            },
            nodeMaxResults: {
                type: "number",
                description: "Max nodes from nodeQuery.",
                default: 20,
            },
            propertyQuery: {
                type: "string",
                description: "Filter properties by NAME or PATH (regex, case-insensitive). A bare name works ('color'), and so does a path from a row's address ('material/map/repeat', or 'material/map' for everything under that slot).",
            },
            propertyType: {
                type: "string",
                description: "Filter by a property's VALUE TYPE (number, boolean, string, color, vector2, matrix, …). NOT a name and NOT its owner: 'material' and 'repeat' are not types and match nothing — use propertyQuery for names. Regex, case-insensitive.",
            },
            includeValues: {
                type: "boolean",
                description: "Include current values in the response. Can be large (geometry data) — combine with a property filter or maxValueSize.",
                default: false,
            },
            maxValueSize: {
                type: "number",
                description: "Max length of each stringified value when includeValues is on; longer ones are truncated.",
                default: 200,
            },
            searchInDescriptions: {
                type: "boolean",
                description: "Also search property descriptions/tooltips, not just names. Slower, more results.",
                default: false,
            },
            maxResults: {
                type: "number",
                description: "Max property rows across ALL targets. The response reports totalMatched and hasMore.",
                default: 50,
            },
            offset: {
                type: "number",
                description: "Rows to skip, for paging.",
                default: 0,
            },
        },
        required: [],
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
        @ILogService private readonly logger: ILogService,
        /** resolves an ADDRESS handle to a live object — including resources, which
         *  no hierarchy lookup can reach */
        @IWebGLService private readonly webgl: IWebGLService | undefined,
    ) { }

    private get engine(): { findObjectById?(id: string): unknown | null } | undefined {
        return this.webgl?.getEngine("threejs") as { findObjectById?(id: string): unknown | null } | undefined;
    }

    /** lazily resolved — absent outside the document products (chrome), where
     *  the target seam degrades to ref-less graph addresses */
    private get targetResolver(): TargetResolverLike | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IObjectTargetService)) as TargetResolverLike;
        } catch {
            return undefined;
        }
    }

    /** the weak resource registry — the element rung for material/texture/geometry
     *  targets, which live in no scene graph */
    private get resources(): { entries(): Iterable<{ deref(): object | undefined }> } | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IResourceService)) as never;
        } catch {
            return undefined;
        }
    }

    /** Lazily resolved — the AI tools are constructed before IInspectionService
     *  is registered (see the same pattern + rationale in ReadCodeTool). */
    private get _inspection(): IInspectionService | null {
        try {
            return this.instantiation.invokeFunction((accessor) => accessor.get(IInspectionService));
        } catch {
            return null;
        }
    }

    async call(parameters?: FindPropertiesParams): Promise<AIToolCallResponse> {
        // No-args is now valid (mode 3: current inspection) — normalize so the
        // filter/pagination reads below don't need optional chaining.
        parameters ??= {};

        if (!this.hierarchy) {
            (this.logger || console).error("Hierarchy service not available.");
            return {
                result: {
                    message: "No project or scene hierarchy available.",
                    success: false,
                }
            }
        }

        const rawTargets = [...(parameters.targets ?? []), parameters.target].filter(t => !!t) as Array<AiTarget | string>;

        // Determine which nodes to query
        let foundNodes: IHierarchyNode[] | null = null;

        if (parameters.nodeQuery) {
            // Mode 2: Search for nodes first
            const query = parameters.nodeQuery.split(',').map(q => q.trim()).filter(q => q.length > 0);
            const nodeMaxResults = parameters.nodeMaxResults ?? 20;
            const nodeTypeRegex = parameters.nodeType ? new RegExp(parameters.nodeType, "i") : null;

            // Search with higher limit if filtering by type
            const searchLimit = nodeTypeRegex ? nodeMaxResults * 3 : nodeMaxResults;
            let searched = await this.hierarchy.search(query, searchLimit) ?? [];

            // Apply type filter
            if (nodeTypeRegex) {
                searched = searched.filter(n => n.type && nodeTypeRegex.test(n.type));
            }

            // Limit to nodeMaxResults
            foundNodes = Array.from(new Set(searched.slice(0, nodeMaxResults)));

            if (foundNodes.length === 0) {
                return {
                    result: {
                        aiMessage: safeStringify({
                            results: [],
                            totalReturned: 0,
                            totalMatched: 0,
                            hasMore: false,
                            message: `No nodes found matching query '${parameters.nodeQuery}'`,
                        }),
                        success: true,
                    }
                }
            }
        }

        // Mode 3: no node params at all → the current inspection targets. This
        // is the ONLY route to unaddressed Resources-panel picks (a geometry, a
        // texture): they are not hierarchy nodes.
        let inspectionRefs: InspectionTargetRef[] = [];
        if (!rawTargets.length && !foundNodes?.length && !parameters?.nodeQuery) {
            inspectionRefs = this._inspection?.getFocusTargets() ?? [];
            if (!inspectionRefs.length) {
                return {
                    result: {
                        message: "Nothing is being inspected — pass targets or nodeQuery, or select/inspect something first.",
                        success: false,
                    },
                };
            }
        }

        if (!rawTargets.length && (!foundNodes || foundNodes.length === 0) && !inspectionRefs.length) {
            throw new Error("No targets provided or found.");
        }

        const regex = parameters.propertyQuery ? new RegExp(parameters.propertyQuery, "i") : null;
        const typeRegex = parameters.propertyType ? new RegExp(parameters.propertyType, "i") : null;
        const includeValues = parameters.includeValues ?? false;
        const searchInDescriptions = parameters.searchInDescriptions ?? false;
        const maxResults = parameters.maxResults ?? 50;
        const offset = parameters.offset ?? 0;
        const maxValueSize = Math.max(10, parameters.maxValueSize ?? 200);

        /** one row per row-address — see isNoiseRow */
        const seenAddresses = new Set<string>();
        const results: unknown[] = [];
        const traversalErrors: Array<{ path: string; error: string }> = [];
        const inspectionErrors: Array<{ target?: AiTarget; nodeName?: string; error: string }> = [];
        /** targets that named nothing — a failed lookup must not read as success */
        const unresolvedTargets: unknown[] = [];
        let totalFound = 0;
        let totalMatched = 0;
        const targetResolver = this.targetResolver;

        // Unified target list: document targets, searched nodes, or inspected
        // objects (mode 3 — the property service accepts live objects).
        const queryTargets: Array<{ node?: IHierarchyNode; ref?: InspectionTargetRef; aiTarget?: AiTarget | string }> =
            rawTargets.length
                ? rawTargets.map(aiTarget => ({ aiTarget }))
                : inspectionRefs.length
                    ? inspectionRefs.map((ref) => ({ ref }))
                    : (foundNodes ?? []).map((node) => ({ node }));

        for (const queryTarget of queryTargets) {
            let target: object;
            let nodeResult: { target?: AiTarget; nodeName?: string; nodeType?: string; source?: string; properties: any[] };
            if (queryTarget.aiTarget !== undefined) {
                // a target may name an object that is no hierarchy node at all —
                // a material or texture from resources_list (element rung)
                const parsed = parseAiTarget(queryTarget.aiTarget);
                const object = parsed ? resolveAiTargetOrResource(targetResolver, this.hierarchy, parsed, this.resources) : null;
                if (!parsed || !object) {
                    unresolvedTargets.push(queryTarget.aiTarget);
                    continue;
                }
                target = object;
                const named = object as { name?: string; type?: string; constructor?: { name?: string } };
                nodeResult = {
                    target: parsed,
                    nodeName: named.name || undefined,
                    nodeType: typeNameOfTarget(named),
                    properties: new Array(),
                };
            }
            else if (queryTarget.node !== undefined) {
                const node = queryTarget.node;
                target = node;
                nodeResult = {
                    // the identity to act with (1h) — node ids are session-internal
                    target: node.owner && typeof node.owner === "object" ? aiTargetFor(targetResolver, this.hierarchy, node.owner) ?? undefined : undefined,
                    nodeName: node.name,
                    nodeType: node.type,
                    properties: new Array(),
                };
            } else {
                const owner = queryTarget.ref!.target as { name?: string; type?: string; constructor?: { name?: string } };
                target = queryTarget.ref!.target;
                nodeResult = {
                    nodeName: queryTarget.ref!.label || owner.name || undefined,
                    nodeType: typeNameOfTarget(owner),
                    source: "current inspection",
                    properties: new Array(),
                };
            }

            let props;
            try {
                props = await this.propertyService.getProperties(target, { scope: PropertyScope.AI_QUERY, viewMode: PropertyViewMode.ALL });
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                this.logger.warn(`[property_find] Failed to inspect '${nodeResult.nodeName ?? nodeResult.nodeType}': ${message}`);
                inspectionErrors.push({ target: nodeResult.target, nodeName: nodeResult.nodeName, error: message });
                continue;
            }
            if (props) {
                let skipped = 0;
                for (const group of props.groups) {
                    for (const p of iterateQueryProperties(group.properties, (path, error) => {
                        const message = error instanceof Error ? error.message : String(error);
                        this.logger.warn(`[property_find] Failed to expand '${path}': ${message}`);
                        if (traversalErrors.length < 10) traversalErrors.push({ path, error: message });
                    })) {
                        // Apply text filter (key, label, PATH, and optionally
                        // description/tooltip).
                        //
                        // The path matters because it is what the caller has been
                        // handed: every row carries one, so "material/map/repeat"
                        // and "material/map" are the natural way to ask. Matching
                        // key and label alone answered those with nothing —
                        // five times in one session, while it held the address.
                        // The dotted form is tried too: a model writes
                        // `material.map` by habit whatever the schema says.
                        if (regex) {
                            const path = splitAddress(p.metadata?.address ?? "")?.path;
                            let matches = regex.test(p.label) || regex.test(p.key)
                                || (!!path && (regex.test(path) || regex.test(path.replace(/\//g, "."))));

                            // Also search in description/tooltip if requested
                            if (!matches && searchInDescriptions) {
                                const tooltip = p.getTooltip ? p.getTooltip() : null;
                                let description = p.metadata?.description;
                                if (typeof description === "string") description = description;
                                else description = description?.value;
                                if (tooltip && regex.test(tooltip)) {
                                    matches = true;
                                } else if (description && regex.test(description)) {
                                    matches = true;
                                }
                            }

                            if (!matches) {
                                continue;
                            }
                        }

                        // Apply type filter
                        if (typeRegex && p.type && !typeRegex.test(p.type)) {
                            continue;
                        }

                        // NOISE: one property arrives once per representation (a vector
                        // AND the raw object behind it), and the raw one stringifies to
                        // "[Circular]" or a mipmap dump. One answer cost 13k characters
                        // and the model never reached the edit it was asked for.
                        const rowAddress = p.metadata?.address;
                        if (rowAddress && seenAddresses.has(rowAddress)) continue;
                        if (isNoiseRow(p) || (includeValues && serializesToCycle(p.value))) continue;
                        if (rowAddress) seenAddresses.add(rowAddress);

                        totalMatched++;

                        // Skip if within offset range
                        if (skipped < offset) {
                            skipped++;
                            continue;
                        }

                        // The cap stops ROWS, not counting: breaking here left
                        // totalMatched equal to totalReturned, so hasMore always
                        // said false and a truncated answer looked complete. The
                        // cap is also global now — one pagination block describing
                        // a per-node limit could not be compared to a summed count.
                        if (totalFound >= maxResults) continue;

                        const propData: any = {
                            // the path WITHIN the object — combined with the result's
                            // `target` this is the full triple; row-level handle
                            // tokens are retired (1h)
                            path: splitAddress(p.metadata?.address ?? "")?.path ?? p.key,
                            propertyKey: p.key,
                            propertyLabel: p.label,
                            propertyType: p.type,
                            groupName: group.name,
                        };

                        if (includeValues) {
                            propData.value = p.value;
                            if (maxValueSize && maxValueSize > 0) {
                                try {
                                    const stringifiedValue = safeStringify(p.value, undefined, safeStringifyReplacer);
                                    if (stringifiedValue.length > maxValueSize) {
                                        propData.value = stringifiedValue.slice(0, maxValueSize) + `... (truncated, total length: ${stringifiedValue.length} chars)`;
                                    }
                                } catch {
                                    // toJSON() or other serialization error — provide a safe fallback
                                    propData.value = `[Unserializable: ${typeof p.value}]`;
                                }
                            }
                        }

                        nodeResult.properties.push(propData);
                        totalFound++;
                    }
                    // every group still contributes to the match count — the cap
                    // only stops rows being added
                }
            }

            results.push(nodeResult);
        }

        const responseData: any = {
            totalReturned: totalFound,
            totalMatched: totalMatched,
            hasMore: totalMatched > (offset + totalFound),
            pagination: {
                offset,
                limit: maxResults
            },
            filters: {
                query: parameters.propertyQuery,
                type: parameters.propertyType,
                includeValues,
                searchInDescriptions
            },
            results,
        };

        if (traversalErrors.length) responseData.traversalErrors = traversalErrors;
        if (inspectionErrors.length) {
            responseData.inspectionErrors = inspectionErrors;
            responseData.hint = "Those targets could not be enumerated. Use property_read with a known path, or inspect a material via its resources_list target.";
        }

        if (unresolvedTargets.length) {
            responseData.unresolvedTargets = unresolvedTargets;
            responseData.hint = "Those targets name nothing now — call hierarchy_search_nodes or resources_list for current ones.";
        }

        // Include node search info if nodeQuery was used
        if (parameters.nodeQuery) {
            responseData.nodeSearch = {
                query: parameters.nodeQuery,
                nodeType: parameters.nodeType,
                nodesFound: foundNodes?.length ?? 0
            };
        }

        let aiMessage: string;
        try {
            aiMessage = safeStringify(responseData, undefined, safeStringifyReplacer);
        } catch {
            // Fallback: strip values that may contain unserializable objects
            for (const r of results as any[]) {
                if (r.properties) {
                    for (const p of r.properties) {
                        if (p.value !== undefined && typeof p.value === 'object') {
                            p.value = `[Unserializable: ${p.value?.constructor?.name || 'object'}]`;
                        }
                    }
                }
            }
            aiMessage = safeStringify(responseData, undefined, safeStringifyReplacer);
        }

        return {
            result: {
                message: responseData,
                aiMessage,
                // every target named nothing: that is a failed lookup, not an empty one
                success: !((unresolvedTargets.length > 0 || inspectionErrors.length > 0) && results.length === 0),
            }
        }
    }

}


// #region Read Property Tool

export interface ReadPropertyParameters {
    /** a document target from a search — `{ref?, address?}` (JSON string tolerated) */
    target?: AiTarget | string;
    /** path of the property within the target ("material/roughness") */
    path?: string;
    /** legacy alias for `path` — accepted, not advertised */
    propertyPath?: string;
    value?: boolean;
    metadata?: boolean;
}

/** Parses `a.b[2].c` into path segments (property names + array indices). */
function parsePropertyPath(path: string): (string | number)[] {
    const segments: (string | number)[] = [];
    for (const part of path.split(".")) {
        const name = part.replace(/\[\d+\]/g, "");
        if (name) segments.push(name);
        for (const index of part.match(/\[(\d+)\]/g) ?? []) {
            segments.push(Number(index.slice(1, -1)));
        }
    }
    return segments;
}

/** Best-effort raw read of `path` on a live object; never throws (host getters
 *  can be hostile). `found` is false when any segment is missing/throws. */
function readRawPath(root: object, path: string): { found: boolean; value?: unknown } {
    let current: unknown = root;
    for (const segment of parsePropertyPath(path)) {
        if (current === null || current === undefined || (typeof current !== "object" && typeof current !== "function")) {
            return { found: false };
        }
        try {
            current = (current as Record<string | number, unknown>)[segment];
        } catch {
            return { found: false };
        }
    }
    return current === undefined ? { found: false } : { found: true, value: current };
}

export class ReadPropertyTool implements FunctionAiTool {

    readonly name = "property_read";
    readonly description = "Read the value or metadata of a specific property. Target it with a TARGET from a search plus a path, or omit the target "
        + "to read from whatever the user is currently inspecting (including Resources-panel picks like a geometry or "
        + "texture). Resolution is resilient: the editor's property system first, then a raw "
        + "object-path read (e.g. 'attributes.position.count' on a geometry) as fallback.";
    readonly type = "function";
    readonly parametersJSONSchema: IJSONSchema = {
        type: "object",
        properties: {
            target: {
                type: "object",
                description: "A TARGET returned by hierarchy_search_nodes, resources_list or property_find, passed back verbatim ({ref?, address?}). Never invent one — search for it.",
            },
            path: {
                type: "string",
                description: "Path of the property to read (e.g. 'material/roughness', 'attributes/position/count').",
            },
            value: {
                type: "boolean",
                description: "Whether to include the property value in the response. Default is true.",
                default: true,
            },
            metadata: {
                type: "boolean",
                description: "Whether to include the property metadata in the response. The metadata includes UI settings, description/tooltip about the property and sometimes available options. Default is false.",
                default: false,
            },
        },
        required: [],
    }

    constructor(
        @IHierarchyService private readonly hierarchy: IHierarchyService,
        @IPropertyService private readonly propertyService: IPropertyService,
        @IInstantiationService private readonly instantiation: IInstantiationService,
        @ILogService private readonly logger: ILogService,
    ) { }

    /** Lazily resolved — the AI tools are constructed before IInspectionService
     *  is registered (see the same pattern + rationale in ReadCodeTool). */
    private get _inspection(): IInspectionService | null {
        try {
            return this.instantiation.invokeFunction((accessor) => accessor.get(IInspectionService));
        } catch {
            return null;
        }
    }

    /** lazily resolved — absent outside the document products (chrome) */
    private get targetResolver(): TargetResolverLike | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IObjectTargetService)) as TargetResolverLike;
        } catch {
            return undefined;
        }
    }

    /** the weak resource registry — the element rung for material/texture/geometry
     *  targets, which live in no scene graph */
    private get resources(): { entries(): Iterable<{ deref(): object | undefined }> } | undefined {
        try {
            return this.instantiation.invokeFunction(accessor => accessor.get(IResourceService)) as never;
        } catch {
            return undefined;
        }
    }

    async call(parameters?: ReadPropertyParameters): Promise<AIToolCallResponse> {
        parameters ??= {};
        const rawPath = parameters.path ?? parameters.propertyPath;
        // a target names the object; the path names the property. Handled before
        // anything else so the rest of this method sees one shape.
        if (parameters.target !== undefined) {
            const parsed = parseAiTarget(parameters.target);
            const object = parsed
                ? resolveAiTargetOrResource(this.targetResolver, this.hierarchy, parsed, this.resources)
                : null;
            if (!parsed || !object) {
                return {
                    result: {
                        message: "The target could not be resolved. Call hierarchy_search_nodes or property_find to get a current target.",
                        aiMessage: safeStringify({ error: "Target did not resolve", target: parameters.target }),
                        success: false,
                    },
                };
            }
            if (!rawPath) {
                return { result: { message: "The target names an object but no property — pass `path` as well.", success: false } };
            }
            return this.readResolved(parsed, object, rawPath, parameters);
        }
        if (!rawPath) {
            return {
                result: {
                    message: "Parameter 'path' is required.",
                    success: false,
                }
            }
        }

        // #region target resolution: the current inspection
        type ReadTarget = { object: object; name?: string; type?: string; source: string };
        const targets: ReadTarget[] = [];
        for (const ref of this._inspection?.getFocusTargets() ?? []) {
            const owner = ref.target as { name?: string; type?: string; constructor?: { name?: string } };
            targets.push({
                object: ref.target,
                name: ref.label || owner.name || undefined,
                type: typeNameOfTarget(owner),
                source: "current inspection",
            });
        }
        if (!targets.length) {
            return {
                result: {
                    message: "Nothing is being inspected — pass a target, or select/inspect something first.",
                    success: false,
                },
            };
        }
        // #endregion

        // Try each target until the path resolves; remember what was tried.
        for (const target of targets) {
            const resolved = await this._resolve(target, rawPath);
            if (!resolved) continue;

            const result: any = {
                nodeName: target.name,
                nodeType: target.type,
                targetSource: target.source,
                path: rawPath,
                resolvedVia: resolved.via,
            };
            if (resolved.property) {
                result.propertyKey = resolved.property.key;
                result.propertyLabel = resolved.property.label;
                result.propertyType = resolved.property.type;
                result.isEditable = resolved.property.editable;
                if (parameters.value !== false) result.value = resolved.property.value;
                if (parameters.metadata) {
                    result.metadata = resolved.property.metadata;
                    result.tooltip = resolved.property.getTooltip ? resolved.property.getTooltip() : null;
                }
            } else {
                result.propertyType = typeof resolved.rawValue;
                result.isEditable = false;
                if (parameters.value !== false) result.value = resolved.rawValue;
            }

            return {
                result: {
                    message: result,
                    aiMessage: safeStringify(result, undefined, safeStringifyReplacer),
                    success: true,
                }
            }
        }

        return {
            result: {
                aiMessage: safeStringify({
                    error: "Property not found",
                    path: rawPath,
                    triedTargets: targets.map(t => ({ name: t.name, type: t.type })),
                    hint: "Use property_find to discover available properties and their paths.",
                }),
                message: `Property '${rawPath}' not found on ${targets.length} target(s).`,
                success: false,
            }
        }
    }

    /**
     * Read through a RESOLVED document target.
     *
     * The object may be a hierarchy node's owner OR something that is not one at
     * all — an orphan-inspected material, a texture from resources_list. The node
     * lookup only enriches the answer (name, type); resolution itself rides
     * `_resolve`'s resilient ladder against the live object.
     */
    private async readResolved(aiTarget: AiTarget, object: object, rawPath: string, parameters: ReadPropertyParameters): Promise<AIToolCallResponse> {
        // tolerance lives at THIS boundary: a model writes dots whatever the schema
        // says, and a path it half-remembered may carry either grammar
        const path = normalizeIncomingPath(rawPath) ?? rawPath;
        const node = this.hierarchy?.find?.(object) ?? null;

        const named = object as { name?: string };
        const target = { object, name: node?.name ?? named.name ?? undefined, type: node?.type ?? typeNameOfTarget(object as never), source: "target" };
        const resolved = await this._resolve(target, path);
        if (!resolved) {
            return {
                result: {
                    message: `Property '${path}' not found on the target. Use property_find to discover available properties and their paths.`,
                    aiMessage: safeStringify({ error: "Property not found", target: aiTarget, path }),
                    success: false,
                },
            };
        }

        const result: any = {
            // the triple BACK, so the model can reuse it verbatim
            target: aiTarget,
            path,
            nodeName: target.name,
            nodeType: target.type,
            resolvedVia: resolved.via,
        };
        if (resolved.property) {
            result.propertyKey = resolved.property.key;
            result.propertyType = resolved.property.type;
            result.isEditable = resolved.property.editable;
            if (parameters.value !== false) result.value = resolved.property.value;
            if (parameters.metadata) result.metadata = resolved.property.metadata;
        }
        else {
            result.propertyType = typeof resolved.rawValue;
            result.isEditable = false;
            if (parameters.value !== false) result.value = resolved.rawValue;
        }
        return { result: { message: result, aiMessage: safeStringify(result, undefined, safeStringifyReplacer), success: true } };
    }

    /**
     * Layered, resilient resolution:
     * 1. the property system's address lookup (hierarchy-addressed nodes only),
     * 2. a walk of the target's property collection (exact address, exact key,
     *    or address suffix — tolerates path prefixes the AI didn't know),
     * 3. a raw object-path read on the live object — works for ANYTHING the
     *    property system doesn't model (e.g. 'attributes.position.count').
     */
    private async _resolve(
        target: { object: object },
        path: string,
    ): Promise<{ via: string; property?: IProperty; rawValue?: unknown } | null> {
        // 1 + 2 are the SHARED ladder property.setValue uses (#152) — reads and
        // writes must resolve identically, or the read tools teach paths the
        // write refuses. The handle is minted FRESH from the live object we
        // just resolved (internal plumbing; it never reaches the model — 1h).
        const resolved = await resolvePropertyByPath(
            this.propertyService, target.object, path, handleOf(target.object) ?? undefined);
        if (resolved) return resolved;

        // 3. raw object path — READ ONLY, and therefore not part of the shared
        // ladder: a raw value has no property to write through.
        const raw = readRawPath(target.object, path);
        if (raw.found) return { via: "raw-object", rawValue: raw.value };
        return null;
    }
}


/**
 * A tool's OWN lists: rows the caller has to read to the end. Summarising these
 * is how a session lost `material/map/repeat` — it sat in the middle of 50
 * property rows, so first-ten/last-ten dropped it and the model concluded the
 * scene could not be edited. Length is bounded by the tools' maxResults, not here.
 */
const RESPONSE_ROW_KEYS = new Set(["results", "properties", "entries", "unresolvedTargets", "children"]);

export function safeStringifyReplacer(key: string, value: any): any {
    if (RESPONSE_ROW_KEYS.has(key) && Array.isArray(value)) {
        return value;
    }
    // We don't want to include large arrays (e.g., geometry data) in the stringified output. This includes binary data represented as Uint8Array, Uint16Array, Float32Array, etc.
    const res = getLargeArrayInfo(value);
    if (res) {
        return res;
    }
    return value;
}


interface LargeArrayLike {
    type: string;
    contentType?: 'binary' | 'array';
    totalLength: number;
    atStart?: Array<any>;
    atEnd?: Array<any>;
}

function getLargeArrayInfo(value: any): string | LargeArrayLike | null {

    if (value && typeof value === 'object') {
        try {
            const constructorName = value.constructor?.name;
            if (['Uint8Array', 'Uint16Array', 'Uint32Array', 'Int8Array', 'Int16Array', 'Int32Array', 'Float32Array', 'Float64Array'].includes(constructorName)) {
                if (value.length > 30) {
                    let atStart = undefined;
                    let atEnd = undefined;
                    let length = value.length;
                    if (isTypedArrayLike(value)) {
                        atStart = Array.from(value.slice(0, 10));
                        atEnd = Array.from(value.slice(-10));
                        return {
                            type: constructorName,
                            contentType: 'array',
                            totalLength: length,
                            atStart,
                            atEnd,
                        };
                    } else if (isArrayBufferLike(value)) {
                        length = value.byteLength;
                        const view = new Uint8Array(value);
                        atStart = Array.from(view.slice(0, 10));
                        atEnd = Array.from(view.slice(-10));
                        return {
                            type: constructorName,
                            contentType: 'binary',
                            totalLength: length,
                            atStart,
                            atEnd,
                        };
                    }
                    else {
                        return {
                            type: constructorName,
                            totalLength: length,
                        };
                    }
                }
            }
            if (Array.isArray(value)) {
                if (value.length > 30) {
                    return {
                        type: 'Array',
                        totalLength: value.length,
                        atStart: value.slice(0, 10),
                        atEnd: value.slice(-10),
                    };
                }
            }
        }
        catch (e) {
            setTimeout(() => {
                console.warn("[Needle] Error in safeStringifyReplacer while checking for large array-like object - throwing delayed. Please report an issue.");
                throw e; // throw async to send to backend logging
            }, 3000);
            return "Error checking large array-like object";
        }
    }


    return null;
}

function isArrayBufferLike(value: any): boolean {
    return value && typeof value === 'object' && typeof value.byteLength === 'number' && typeof value.slice === 'function';
}
function isTypedArrayLike(value: any): boolean {
    return value && typeof value === 'object' && typeof value.length === 'number' && isArrayBufferLike(value.buffer);
}
